import { cn } from '@heidi/utils';
import { useEffect, useRef } from 'react';

const BAR_COUNT = 56;
const MIN_HEIGHT_PX = 3;
const MAX_HEIGHT_PX = 26;

interface VoiceBarVisualizerProps {
  amplitude: number;
  bands: [number, number, number];
  /** Keep a soft idle pulse while the session is live. */
  active: boolean;
  className?: string;
  /** Bar fill — defaults to currentColor (theme text-secondary). */
  color?: string;
}

/**
 * Compact horizontal equaliser (OpenAI / Siri voice vibe): mirrored envelope,
 * live mic amplitude, soft edge fade. DOM heights updated on rAF — no React
 * re-render per frame.
 */
export function VoiceBarVisualizer({
  amplitude,
  bands,
  active,
  className,
  color,
}: VoiceBarVisualizerProps) {
  const barsRef = useRef<(HTMLDivElement | null)[]>([]);
  const ampRef = useRef(amplitude);
  const bandsRef = useRef(bands);
  const activeRef = useRef(active);
  const phaseRef = useRef(0);

  ampRef.current = amplitude;
  bandsRef.current = bands;
  activeRef.current = active;

  useEffect(() => {
    let rafId = 0;

    const tick = () => {
      phaseRef.current += activeRef.current ? 0.11 : 0.04;
      const amp = Math.min(1, Math.max(0, ampRef.current) * 3.2);
      const [b0, b1, b2] = bandsRef.current;
      const bandBoost = (b0 + b1 + b2) / 3;
      // Floor so idle listening still breathes; mute/inactive stays flatter.
      const energy = activeRef.current
        ? Math.max(0.12, amp * 0.75 + bandBoost * 0.9)
        : 0.06;

      for (let i = 0; i < BAR_COUNT; i++) {
        const bar = barsRef.current[i];
        if (!bar) continue;

        const t = i / (BAR_COUNT - 1);
        // Cosine dome — taller in the middle, feathered at the ends (Siri-ish).
        const envelope = Math.pow(Math.cos((t - 0.5) * Math.PI), 1.35);
        const wobble =
          0.55 + 0.45 * Math.sin(phaseRef.current * 1.1 + i * 0.38) * (0.4 + energy);
        const height =
          MIN_HEIGHT_PX + (MAX_HEIGHT_PX - MIN_HEIGHT_PX) * envelope * energy * wobble;

        bar.style.height = `${height.toFixed(1)}px`;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <div
      className={cn(
        'flex h-8 w-full items-center justify-center gap-px overflow-hidden text-text-secondary',
        '[mask-image:linear-gradient(90deg,transparent,black_6%,black_94%,transparent)]',
        className
      )}
      aria-hidden
    >
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        <div
          key={i}
          ref={(el) => {
            barsRef.current[i] = el;
          }}
          className="min-w-[2px] max-w-[4px] flex-1 rounded-full"
          style={{
            height: MIN_HEIGHT_PX,
            backgroundColor: color ?? 'currentColor',
            transition: 'height 60ms linear',
          }}
        />
      ))}
    </div>
  );
}
